// @flow
import React from 'react';
import {
  convertUTCToLocalDate,
  convertLocalToUTCDate,
  sfdcQuoteIdValidation,
  sfdcOpportunityIdValidation,
  commitAmountValidation,
} from '@src/common-utils/utils';
import { convertConfigArrayToMap } from '@src/configuration/utils';
import {
  BOOLEAN_SELECT_INPUT_TYPE,
  CHECKBOX_INPUT_TYPE,
  DATE_INPUT_TYPE,
  SELECT_INPUT_TYPE,
  TEXT_INPUT_TYPE,
  TEXT_NUMBER_INPUT_TYPE,
} from '@src/formik-utils/consts';
import { BACKEND_FIELDNAMES, FRONT_END_DISPLAY_NAMES, FORM_NAMES } from '@streaming-projects/enums';
import { string, date, array } from 'yup';
import { Tooltip } from '@presentational/Tooltip';
import { SP_CREDITS_AND_BURSTS_JSON_CONFIG } from '@streaming-projects/credits-and-bursts/config';
import { IS_MLD_IN_COMMIT_LIVE } from '@streaming-projects/constants';
import {
  CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  DEAL_TYPE_RENEWAL,
  GLOBAL_DISCOUNT_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME,
  RATE_CARD_VERSION_8_14_2025,
  RATE_CARD_VERSION_5_15_2026,
} from '@src/constants';
import { COMMITTED_CAPACITY_JSON_CONFIG } from '@streaming-projects/user-commit-details/committed-capacity/config';
import { formatMLDIntoFrontEndFormat } from '@components/custom-discounts/utils';
import { spCustomDiscountsValidation } from '@streaming-projects/custom-discounts/validation';

export const SP_USER_COMMIT_GENERIC_DETAILS_CONFIG = [
  {
    displayName: FRONT_END_DISPLAY_NAMES.COMMIT_NAME,
    backendFieldName: BACKEND_FIELDNAMES.NAME,
    inputType: TEXT_INPUT_TYPE,
    validation: string()
      .label(FRONT_END_DISPLAY_NAMES.COMMIT_NAME)
      .required()
      .notOneOf(
        [FRONT_END_DISPLAY_NAMES.DEFAULT_COMMIT_NAME],
        `Commit name cannot be "${FRONT_END_DISPLAY_NAMES.DEFAULT_COMMIT_NAME}"`
      ),
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.COMMIT_START_DATE,
    backendFieldName: BACKEND_FIELDNAMES.COMMIT_START_DATE,
    validation: date().label(FRONT_END_DISPLAY_NAMES.COMMIT_START_DATE).required(),
    inputType: DATE_INPUT_TYPE,
    inputTransformationFunc: convertUTCToLocalDate,
    transformationFunctionToSendToBackEnd: convertLocalToUTCDate,
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.COMMIT_END_DATE,
    backendFieldName: BACKEND_FIELDNAMES.COMMIT_END_DATE,
    validation: date().label(FRONT_END_DISPLAY_NAMES.COMMIT_END_DATE).required(),
    inputType: DATE_INPUT_TYPE,
    inputTransformationFunc: convertUTCToLocalDate,
    transformationFunctionToSendToBackEnd: convertLocalToUTCDate,
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.DEAL_TYPE,
    backendFieldName: BACKEND_FIELDNAMES.DEAL_TYPE,
    inputType: SELECT_INPUT_TYPE,
    validation: string().label(FRONT_END_DISPLAY_NAMES.DEAL_TYPE).required(),
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.MARKETPLACE,
    backendFieldName: BACKEND_FIELDNAMES.MARKETPLACE,
    inputType: SELECT_INPUT_TYPE,
    validation: string().label(FRONT_END_DISPLAY_NAMES.MARKETPLACE).required(),
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.PAYMENT_SCHEDULE,
    backendFieldName: BACKEND_FIELDNAMES.PAYMENT_SCHEDULE,
    inputType: SELECT_INPUT_TYPE,
    validation: string().label(FRONT_END_DISPLAY_NAMES.PAYMENT_SCHEDULE).required(),
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  // CTODO::SP Confirm if Rate Card is Required Field
  {
    displayName: FRONT_END_DISPLAY_NAMES.RATE_CARD,
    backendFieldName: BACKEND_FIELDNAMES.RATE_CARD,
    inputType: SELECT_INPUT_TYPE,
    disabled: true,
    tooltip: <Tooltip text="Rate Card is editable from Org Page" />,
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
    validation: string()
      .label(FRONT_END_DISPLAY_NAMES.RATE_CARD)
      .required()
      .test(
        'renewal-rate-card-validation',
        'This rate card cannot be used for renewals. Please update the rate card from the ORG page to modify the deal type.',
        function (value) {
          const { deal_type: dealType } = this.parent;
          // When Deal Type is Renewal, RC cannot be the new 8/14 or 5/15/2026 rate card as it is not supported, if it is, then it is an error
          return !(
            dealType === DEAL_TYPE_RENEWAL &&
            (value === RATE_CARD_VERSION_8_14_2025 || value === RATE_CARD_VERSION_5_15_2026)
          );
        }
      ),
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.CREDITS_AND_BURSTS,
    backendFieldName: BACKEND_FIELDNAMES.CREDITS_AND_BURSTS,
    inputType: BOOLEAN_SELECT_INPUT_TYPE,
    validation: string().label(FRONT_END_DISPLAY_NAMES.CREDITS_AND_BURSTS).required(),
    defaultValue: false,
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.SUPPORT_TIER,
    backendFieldName: BACKEND_FIELDNAMES.SUPPORT_TIER,
    inputType: SELECT_INPUT_TYPE,
    validation: string().label(FRONT_END_DISPLAY_NAMES.SUPPORT_TIER).required(),
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.NET_NEW_CUSTOMER,
    backendFieldName: BACKEND_FIELDNAMES.NET_NEW_CUSTOMER,
    inputType: CHECKBOX_INPUT_TYPE,
    defaultValue: false,
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.MSK_TAKEOUT,
    backendFieldName: BACKEND_FIELDNAMES.MSK_TAKEOUT,
    inputType: CHECKBOX_INPUT_TYPE,
    defaultValue: false,
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.COMMITTED_CAPACITY,
    backendFieldName: BACKEND_FIELDNAMES.COMMITTED_CAPACITY,
    inputType: BOOLEAN_SELECT_INPUT_TYPE,
    validation: string().label(FRONT_END_DISPLAY_NAMES.COMMITTED_CAPACITY).required(),
    defaultValue: false,
    belongingForm: FORM_NAMES.USER_COMMIT.GENERIC_DETAILS_FORM,
  },
];

export const SP_USER_COMMIT_SFDC_CONTRACT_DETAILS_CONFIG = [
  {
    displayName: FRONT_END_DISPLAY_NAMES.SFDC_OPPORTUNITY_ID,
    backendFieldName: BACKEND_FIELDNAMES.SFDC_OPPORTUNITY_ID,
    inputType: TEXT_INPUT_TYPE,
    validation: sfdcOpportunityIdValidation.label(FRONT_END_DISPLAY_NAMES.SFDC_OPPORTUNITY_ID),
    maxLength: 18,
    belongingForm: FORM_NAMES.USER_COMMIT.SFDC_CONTRACT_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.SFDC_QUOTE_NAME,
    backendFieldName: BACKEND_FIELDNAMES.SFDC_QUOTE_NAME,
    inputType: TEXT_INPUT_TYPE,
    validation: sfdcQuoteIdValidation.label(FRONT_END_DISPLAY_NAMES.SFDC_QUOTE_NAME),
    belongingForm: FORM_NAMES.USER_COMMIT.SFDC_CONTRACT_DETAILS_FORM,
  },
  {
    displayName: FRONT_END_DISPLAY_NAMES.SFDC_OPPORTUNITY_NAME,
    backendFieldName: BACKEND_FIELDNAMES.SFDC_OPPORTUNITY_NAME,
    inputType: TEXT_INPUT_TYPE,
    validation: string().label(FRONT_END_DISPLAY_NAMES.SFDC_OPPORTUNITY_NAME),
    belongingForm: FORM_NAMES.USER_COMMIT.SFDC_CONTRACT_DETAILS_FORM,
  },
];

export const SP_USER_COMMIT_AMOUNT_DETAILS_CONFIG = [
  {
    displayName: FRONT_END_DISPLAY_NAMES.COMMIT_AMOUNT,
    backendFieldName: BACKEND_FIELDNAMES.COMMIT_AMOUNT,
    inputType: TEXT_NUMBER_INPUT_TYPE,
    tooltip: <Tooltip text="Commit amount signed by the customer." />,
    validation: commitAmountValidation,
    belongingForm: FORM_NAMES.USER_COMMIT.COMMIT_AMOUNT_DETAILS_FORM,
  },
];

const SP_USER_COMMIT_SP_SELECTION_CONFIG = [
  {
    displayName: FRONT_END_DISPLAY_NAMES.IS_SP_SELECTED,
    backendFieldName: BACKEND_FIELDNAMES.COMMIT_SP_SELECTION_IDS,
    inputType: 'array',
    validation: array(),
    belongingForm: FORM_NAMES.USER_COMMIT.SP_SELECTION_FORM,
  },
];

export const SP_USER_COMMIT_DETAILS_CONFIG = [
  ...SP_USER_COMMIT_GENERIC_DETAILS_CONFIG,
  ...SP_USER_COMMIT_SFDC_CONTRACT_DETAILS_CONFIG,
  ...SP_USER_COMMIT_AMOUNT_DETAILS_CONFIG,
  ...SP_USER_COMMIT_SP_SELECTION_CONFIG,
];

export const SP_CUSTOM_DISCOUNTS_JSON_CONFIG = {
  backendFieldName: CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  inputTransformationFunc: (metricsDiscounts) => {
    return {
      [GLOBAL_DISCOUNT_BACKEND_NAME]: Number(metricsDiscounts?.global_discount ?? 0.0),
      [METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME]: formatMLDIntoFrontEndFormat(
        metricsDiscounts?.metrics ?? []
      ),
    };
  },
  validation: spCustomDiscountsValidation,
  belongingForm: FORM_NAMES.CUSTOM_DISCOUNTS.CUSTOM_DISCOUNTS_FORM,
};

export const SP_USER_COMMIT_PAGE_CONFIG = [
  ...SP_USER_COMMIT_DETAILS_CONFIG,
  SP_CREDITS_AND_BURSTS_JSON_CONFIG,
  ...(IS_MLD_IN_COMMIT_LIVE ? [SP_CUSTOM_DISCOUNTS_JSON_CONFIG] : []),
  COMMITTED_CAPACITY_JSON_CONFIG,
];

export const SP_USER_COMMIT_DETAILS_CONFIG_MAP = convertConfigArrayToMap(
  SP_USER_COMMIT_DETAILS_CONFIG
);
